import React, { useContext } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import { useMutation } from '@apollo/client';
import * as Yup from 'yup';

import UserContext from '../../../contexts/UserContext';
import Input from '../../../components/Input';
import { useCustomToast } from '../../../hooks/useCustomToast';
import { INTERNAL_CONSIGNEE_SCAN } from './query';

interface ConsigneeScanModalProps {
  isOpen: boolean;
  onClose: () => void;
  refetch: () => void;
  trackingNumber: string;
  accessPointID: string;
  epraIndicator?: boolean;
}

const ConsigneeScanModal: React.FC<ConsigneeScanModalProps> = ({
  isOpen,
  onClose,
  refetch,
  trackingNumber,
  accessPointID,
  epraIndicator = false,
}) => {
  const [internalConsigneeScan, { loading }] = useMutation(INTERNAL_CONSIGNEE_SCAN);
  const { user } = useContext(UserContext);
  const toast = useCustomToast();

  // Dynamic validation schema based on whether EPRA pin is required
  const ConsigneeScanSchema = Yup.object().shape({
    comment: Yup.string().required('Du musst einen Kommentar eingeben'),
    pin: epraIndicator
      ? Yup.string()
        .required('EPRA PIN ist erforderlich')
        .matches(/^\d+$/, 'PIN darf nur Zahlen enthalten')
      : Yup.string(),
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Consignee Pickup Scan simulieren</ModalHeader>

        <Formik
          initialValues={{
            comment: '',
            pin: '',
          }}
          validationSchema={ConsigneeScanSchema}
          onSubmit={async ({ comment, pin }) => {
            try {
              const result = await internalConsigneeScan({
                variables: {
                  trackingNumber,
                  accessPointID,
                  comment,
                  internalUserId: user.id,
                  pin: epraIndicator ? pin : undefined,
                },
              });

              if (result.data?.internalConsigneePickupScan === 'success') {
                toast({
                  title: 'Erfolgreich',
                  description: 'Consignee Pickup Scan wurde erfolgreich ausgelöst',
                  status: 'success',
                });
                await refetch();
                onClose();
              } else {
                toast({
                  title: 'Fehler',
                  description: 'Der Scan konnte nicht durchgeführt werden. Überprüfe den Paketstatus.',
                  status: 'error',
                });
              }
            } catch (error: any) {
              toast({
                title: 'Fehler',
                description: error.message || 'Ein unbekannter Fehler ist aufgetreten',
                status: 'error',
              });
            }
          }}>
          {({ isSubmitting }) => (
            <Form>
              <ModalBody>
                <Text mb="4" fontSize="sm">
                  Du bist dabei, einen Consignee Pickup Scan für das Paket <strong>{trackingNumber}</strong> zu
                  simulieren. Bitte gib einen Kommentar ein, warum du diese Aktion durchführst.
                </Text>
                <Input name="comment" label="Kommentar" placeholder="Grund für die Simulation..." />

                {epraIndicator && (
                  <>
                    <Input name="pin" label="EPRA PIN" placeholder="PIN eingeben..." />
                    <Alert status="warning" mt="2" borderRadius="md">
                      <AlertIcon />
                      <AlertDescription fontSize="sm">
                        <strong>Achtung:</strong> Für den EPRA PIN sind maximal 3 Versuche erlaubt.
                        Danach wird das Paket gesperrt.
                      </AlertDescription>
                    </Alert>
                  </>
                )}
              </ModalBody>

              <ModalFooter>
                <Button variant="ghost" size="sm" colorScheme="red" type="button" onClick={onClose} isDisabled={loading}>
                  Abbrechen
                </Button>
                <Button ml="4" size="sm" colorScheme="brand" type="submit" isLoading={loading || isSubmitting}>
                  Scan ausführen
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
        <ModalCloseButton />
      </ModalContent>
    </Modal>
  );
};

export default ConsigneeScanModal;
